import { Switch } from "antd";
import { type SwitchProps } from "antd/lib/switch";
import { memo } from "react";

import { cn } from "@btnet/utilities-core";

import { useTracking } from "utilities/analytics/analytics";

import "./BTSwitch.less";

export declare type BTSwitchSize = "small" | "default" | "large";

export interface IBTSwitchProps extends Omit<SwitchProps, "onChange" | "size"> {
    id: string;
    "data-testid": string;

    label?: string | JSX.Element;

    /** Pass setFieldValue */
    onCheckedChange: (field: string, value: boolean) => void;

    /**
     * @default light
     */
    theme?: "light" | "dark";

    /**
     * @default default
     */
    size?: BTSwitchSize;

    /**
     * Additional class name for the switch element
     * @default ""
     */
    switchClassName?: string;
}

function BTSwitchInternal({
    id,
    label,
    onCheckedChange,
    disabled,
    size = "default",
    theme = "light",
    className,
    switchClassName,
    ...restProps
}: IBTSwitchProps) {
    const { trackEvent, Track } = useTracking({
        element: "Switch",
        uniqueId: restProps["data-testid"],
    });

    const handleChange = (checked: boolean) => {
        onCheckedChange(id, checked);
        trackEvent({
            event: "ValueChange",
            extraInfo: {
                checked: checked,
            },
        });
    };

    const isLarge = size === "large";

    return (
        <Track>
            <div
                className={cn(
                    "BTSwitch",
                    {
                        "BTSwitch-disabled": disabled,
                        "BTSwitch-dark": theme === "dark",
                    },
                    `BTSwitch-${size}`,
                    className,
                )}
            >
                <label>
                    {/* eslint-disable-next-line react/forbid-elements */}
                    <Switch
                        className={cn("BTSwitch-input", switchClassName)}
                        onChange={handleChange}
                        disabled={disabled}
                        size={isLarge ? "default" : size}
                        {...restProps}
                    />
                    {label}
                </label>
            </div>
        </Track>
    );
}

export const BTSwitch = memo(BTSwitchInternal);
